// tts_job_poll — wait for an async text-to-speech job by polling the generic
// job-status endpoint, mirroring the proven NarrationJobTracker poll loop.
//
// The TTS backend (POST api/course/text-to-speech) returns 202 { jobId } and
// the audio is read from the job result once the worker reports
// status:"completed". The job-status endpoint (GET api/jobs/{id}) lives on
// MW_API_URL (localhost:4000 in dev) — the same host the avatar/narration tabs
// poll. Response shape: { status, progress, result, error }, where for a
// text-to-speech job result = { success, audioUrl, duration, voice, provider,
// fileId, timestamp }.

import { authedFetch } from "./http_service";

const POLL_INTERVAL = 3000;

// Job-status endpoint base: MW_API_URL with a guaranteed trailing slash.
function resolveJobsBase() {
  const b = process.env.MW_API_URL || "";
  return b.endsWith("/") ? b : `${b}/`;
}

// Pull the audio URL + metadata out of a completed job's result.
function extractAudio(result) {
  const r = (result && typeof result === "object" && result) || {};
  const audioUrl =
    (typeof r.audioUrl === "string" && r.audioUrl) || (typeof r.url === "string" && r.url) || "";
  if (!audioUrl) return null;
  return {
    audioUrl,
    duration: typeof r.duration === "number" ? r.duration : 0,
    voice: r.voice,
    provider: r.provider,
    fileId: r.fileId,
  };
}

/**
 * Poll GET {MW_API_URL}api/jobs/{jobId} until the job completes.
 *
 * @param {string} jobId  the jobId returned by the 202 submit response
 * @param {{ timeoutMs?: number }} [opts]  give up after this long (default 5 min)
 * @returns {Promise<{ audioUrl, duration, voice, provider, fileId }>}
 *   Rejects with Error("no-job" | "timeout" | "no-audio" | <backend error>).
 */
/**
 * Generic job poller — GET {MW_API_URL}api/jobs/{jobId} until completion,
 * resolving with the raw job `result` (no TTS audio extraction). Used by the
 * batch question generator whose result is { total, successCount, questions[] }.
 *
 * @param {string} jobId
 * @param {{ timeoutMs?: number }} [opts]  default 10 min (N soru × retry loop)
 * @returns {Promise<any>} the job result object.
 *   Rejects with Error("no-job" | "timeout" | <backend error>).
 */
export function waitForJob(jobId, { timeoutMs = 600000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!jobId) {
      reject(new Error("no-job"));
      return;
    }
    const url = `${resolveJobsBase()}api/jobs/${jobId}`;
    const maxAttempts = Math.max(1, Math.ceil(timeoutMs / POLL_INTERVAL));
    let attempts = 0;
    let timer = null;
    let settled = false;

    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      fn(arg);
    };

    const tick = async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        settle(reject, new Error("timeout"));
        return;
      }
      try {
        const res = await authedFetch(url, {
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.status === "completed") {
            settle(resolve, data.result);
            return;
          }
          if (data.status === "failed") {
            settle(reject, new Error(data.error || "failed"));
            return;
          }
        }
      } catch {
        // network blip — keep polling
      }
      timer = setTimeout(tick, POLL_INTERVAL);
    };

    tick();
  });
}

export function waitForTtsJob(jobId, { timeoutMs = 300000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!jobId) {
      reject(new Error("no-job"));
      return;
    }
    const url = `${resolveJobsBase()}api/jobs/${jobId}`;
    const maxAttempts = Math.max(1, Math.ceil(timeoutMs / POLL_INTERVAL));
    let attempts = 0;
    let timer = null;
    let settled = false;

    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      fn(arg);
    };

    const tick = async () => {
      attempts += 1;
      if (attempts > maxAttempts) {
        settle(reject, new Error("timeout"));
        return;
      }
      try {
        const res = await authedFetch(url, {
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.status === "completed") {
            const audio = extractAudio(data.result);
            if (!audio) {
              settle(reject, new Error("no-audio"));
              return;
            }
            settle(resolve, audio);
            return;
          }
          if (data.status === "failed") {
            settle(reject, new Error(data.error || "failed"));
            return;
          }
          // pending / processing → keep polling
        }
        // non-OK response (e.g. job not registered yet) → keep polling
      } catch {
        // network blip — keep polling
      }
      timer = setTimeout(tick, POLL_INTERVAL);
    };

    tick();
  });
}
